import {
  useOverdueHabits,
  useOverdueRepeatingTasks,
  useOverdueTasks,
  useTodaysDeadlineDeadlineTasks,
  useTodaysHabits,
  useTodaysRepeatingTasks,
  useTodaysTodos,
} from 'features/tasks';
import uniqBy from 'lodash/uniqBy';
import { useMemo } from 'react';
import {
  useRelationships,
  useRelationshipsMap,
} from 'shared/hooks/use-relationships';
import { useUser } from 'shared/hooks/use-user';
import { ID, IDPrefixes } from 'shared/types/id';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import {
  createPrefixId,
  getPrefixedId,
  getPrefixedIdPrefix,
} from 'shared/utils/prefix-id';

export type Options = {
  includeFilters?: boolean;
  includeCompleted?: boolean;
  ignoreHideHabits?: boolean;
};

const defaultOptions: Options = {
  includeFilters: true,
  includeCompleted: false,
  ignoreHideHabits: false,
};

export const useTodayTasks = ({
  includeFilters,
  includeCompleted,
  ignoreHideHabits,
} = defaultOptions) => {
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const todos = useTodaysTodos({ uncompletedOnly: !includeCompleted });
  const repeatingTasks = useTodaysRepeatingTasks(weekStartsOn);
  const habits = useTodaysHabits(weekStartsOn);
  const overdueTodos = useOverdueTasks();
  const overdueHabits = useOverdueHabits(weekStartsOn);
  const overdueRepeatingTasks = useOverdueRepeatingTasks(weekStartsOn);
  const deadlineDeadlineTasks = useTodaysDeadlineDeadlineTasks();

  const relationships = useRelationships();
  const relationshipsMap = useRelationshipsMap(relationships);

  return useMemo(() => {
    const tasks = uniqBy(
      user?.settings?.todayHideHabits && !ignoreHideHabits
        ? [
            ...overdueTodos,
            ...overdueRepeatingTasks,
            ...todos,
            ...repeatingTasks,
            ...deadlineDeadlineTasks,
          ]
        : [
            ...overdueTodos,
            ...overdueRepeatingTasks,
            ...overdueHabits,
            ...todos,
            ...repeatingTasks,
            ...habits,
            ...deadlineDeadlineTasks,
          ],
      'id',
    );

    if (!user?.settings?.todayFilterByTags?.length || !includeFilters) {
      return tasks;
    }

    const tagTaskIds = user.settings.todayFilterByTags
      .map((tagId) =>
        relationshipsMap[createPrefixId(tagId, IDPrefixes.Tag)]?.reduce<ID[]>(
          (acc, prefixedId) => {
            if (getPrefixedIdPrefix(prefixedId) === IDPrefixes.Task) {
              acc.push(getPrefixedId(prefixedId));
            }

            return acc;
          },
          [],
        ),
      )
      .flat(1);

    return tasks.filter(({ id }) => tagTaskIds.includes(id));
  }, [
    user?.settings?.todayHideHabits,
    user?.settings?.todayFilterByTags,
    overdueTodos,
    overdueRepeatingTasks,
    todos,
    repeatingTasks,
    deadlineDeadlineTasks,
    overdueHabits,
    habits,
    includeFilters,
    relationshipsMap,
    ignoreHideHabits,
  ]);
};
